import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, GripVertical, CircleOff } from 'lucide-react';
import type { ProgramLine } from '../../utils/useStatementModel';
import { centsToDollars } from '../../utils/useStatementModel';
import { DrilldownRows } from './DrilldownRows';

interface StatementRowProps {
  line: ProgramLine;
  expanded: boolean;
  onToggleExpand: () => void;
  onShareOverride: (pct: number | null) => void;
  onExclude: () => void;
}

/**
 * One program row: drag handle (reorder), expander (payment drill-down),
 * per-row share override (marked *, one-click reset), exclude control.
 */
export function StatementRow({ line, expanded, onToggleExpand, onShareOverride, onExclude }: StatementRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: line.programId,
  });

  return (
    <>
      <tr
        ref={setNodeRef}
        style={{ transform: CSS.Transform.toString(transform), transition }}
        className={`border-t border-gray-100 text-sm ${isDragging ? 'relative z-10 bg-emerald-50 shadow' : 'bg-white'}`}
      >
        <td className="w-8 whitespace-nowrap py-1.5 pl-1 text-gray-300">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab rounded p-0.5 hover:bg-gray-100 hover:text-gray-500 print:hidden"
            aria-label={`Reorder ${line.programName}`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
        <td className="py-1.5 pr-2">
          <button
            type="button"
            onClick={onToggleExpand}
            className="flex items-center gap-1 text-left text-gray-900 hover:text-emerald-700"
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            )}
            <span className="min-w-0 truncate">{line.programName}</span>
            <span className="ml-1 shrink-0 text-xs text-gray-400">({line.nPayments})</span>
          </button>
        </td>
        <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900">${centsToDollars(line.baseCents)}</td>
        <td className="w-24 py-1.5 pr-1 text-right">
          <span className="inline-flex items-center gap-0.5">
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={Number((line.sharePct * 100).toFixed(2))}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v >= 0 && v <= 100) onShareOverride(v / 100);
              }}
              className={`w-14 rounded border px-1 py-0.5 text-right text-xs tabular-nums focus:border-emerald-500 focus:outline-none ${
                line.overridden ? 'border-amber-400 bg-amber-50 font-medium' : 'border-gray-200'
              }`}
              aria-label={`Share % for ${line.programName}`}
            />
            {line.overridden ? (
              <button
                type="button"
                onClick={() => onShareOverride(null)}
                title="Reset to global share"
                className="text-amber-600 hover:text-amber-800"
              >
                *
              </button>
            ) : (
              <span className="w-2" />
            )}
          </span>
        </td>
        <td className="py-1.5 pr-2 text-right font-medium tabular-nums text-gray-900">
          ${centsToDollars(line.rentCents)}
        </td>
        <td className="w-8 py-1.5 pr-1 text-right print:hidden">
          <button
            type="button"
            onClick={onExclude}
            title={`Exclude ${line.programName} from the statement`}
            className="rounded p-0.5 text-gray-300 hover:bg-gray-100 hover:text-red-600"
          >
            <CircleOff className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {expanded && <DrilldownRows rows={line.drilldown} />}
    </>
  );
}
